import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import StylizedQRCodeDisplay from './StylizedQRCodeDisplay';

/**
 * CustomQRDisplay Component
 *
 * Shows QR codes in a swipe carousel. Order:
 *   1. Stencil halftone QR (2-level high contrast, from profile photo)
 *   2. Custom static PNGs (probed from public/images/qr/)
 *   3. Gradient QR (always present, rendered by StylizedQRCodeDisplay)
 *
 * Halftone generation uses dynamic import — the ~60KB module is a separate
 * webpack chunk that only loads when this component mounts with a photoUrl.
 *
 * The halftone's photo is the People square when the photo has its stored
 * face box (photoMeta — Drop 3 request 1), else the original centre crop.
 */
const CustomQRDisplay = ({ profileUserId, fallbackUrl, photoUrl, photoMeta, size = 300 }) => {
  const [customImages, setCustomImages] = useState([]);
  const [halftoneImage, setHalftoneImage] = useState(null);
  const [halftoneLoading, setHalftoneLoading] = useState(!!photoUrl);
  const [currentIndex, setCurrentIndex] = useState(() => {
    const saved = localStorage.getItem('qr_carousel_index');
    return saved !== null ? parseInt(saved, 10) : 0;
  });

  // Touch tracking for swipe
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const [slideDirection, setSlideDirection] = useState(0); // -1 left, 1 right

  // Probe for custom images in background (PNG only, by profileUserId)
  useEffect(() => {
    if (!profileUserId) return;

    const probeImages = async () => {
      const images = [];
      let i = 1;
      const maxImages = 10; // Safety limit

      while (i <= maxImages) {
        const url = `/images/qr/${profileUserId}_${i}.png`;
        const exists = await checkImageExists(url);

        if (!exists) break;
        images.push(url);
        i++;
      }

      if (images.length > 0) {
        setCustomImages(images);
      }
    };

    probeImages();
  }, [profileUserId]);

  // Only the crop's inputs, as a string: an equal meta, or a change that can't
  // move the crop (colour, card copy…), never regenerates. The crop maths loads
  // with the halftone chunk — never on the app's first paint.
  const metaKey = JSON.stringify(
    photoMeta ? { src: photoMeta.src, aspect: photoMeta.aspect, face: photoMeta.face } : null
  );

  // Generate halftone QR from profile photo
  useEffect(() => {
    if (!photoUrl || !fallbackUrl) return;

    let cancelled = false;

    const generate = async () => {
      try {
        const [{ createHalftoneQR }, { qrPhotoRegion }] = await Promise.all([
          import('../../services/qr/halftoneQR'),
          import('../../services/qr/qrPhotoRegion'),
        ]);
        const region = qrPhotoRegion(photoUrl, JSON.parse(metaKey));
        const dataUrl = await createHalftoneQR(photoUrl, fallbackUrl, 'stencil', { region });
        if (!cancelled) {
          setHalftoneImage(dataUrl);
          setHalftoneLoading(false);
        }
      } catch (err) {
        console.error('[CustomQRDisplay] Halftone generation failed:', err);
        if (!cancelled) setHalftoneLoading(false);
      }
    };

    generate();
    return () => {
      cancelled = true;
    };
  }, [photoUrl, fallbackUrl, metaKey]);

  // Check if an image exists by attempting to load it
  const checkImageExists = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  };

  // Build carousel items: halftone → custom PNGs → gradient (as 'generated' marker)
  const carouselImages = [...(halftoneImage ? [halftoneImage] : []), ...customImages];
  const totalItems = carouselImages.length + 1; // +1 for gradient QR
  const generatedIndex = carouselImages.length; // gradient QR is always last

  // Handle touch start
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  // Handle touch move
  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  // Handle touch end - determine swipe direction
  const handleTouchEnd = () => {
    const swipeThreshold = 50;
    const diff = touchStartX.current - touchEndX.current;

    if (Math.abs(diff) > swipeThreshold && totalItems > 1) {
      if (diff > 0) {
        setSlideDirection(1);
        setCurrentIndex((prev) => {
          const next = (prev + 1) % totalItems;
          localStorage.setItem('qr_carousel_index', next);
          return next;
        });
      } else {
        setSlideDirection(-1);
        setCurrentIndex((prev) => {
          const next = (prev - 1 + totalItems) % totalItems;
          localStorage.setItem('qr_carousel_index', next);
          return next;
        });
      }
    }

    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  // Clamp saved index to valid range (carousel size may differ from last session)
  const clampedIndex = currentIndex >= totalItems ? 0 : currentIndex;
  if (clampedIndex !== currentIndex) {
    setCurrentIndex(clampedIndex);
    localStorage.setItem('qr_carousel_index', clampedIndex);
  }

  // While halftone is loading, show empty space at the right size (no flicker to gradient)
  if (halftoneLoading) {
    return <div style={{ width: size, height: size }} />;
  }

  // Single item only (no halftone, no custom PNGs) — just show gradient QR
  if (totalItems === 1) {
    return <StylizedQRCodeDisplay url={fallbackUrl} size={size} />;
  }

  // Standard Framer Motion carousel variants
  const slideVariants = {
    enter: (dir) => ({ x: dir > 0 ? 200 : -200, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? -200 : 200, opacity: 0 }),
  };

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        width: size,
        height: size,
        position: 'relative',
        overflow: 'hidden',
        cursor: 'grab',
        userSelect: 'none',
      }}
    >
      <AnimatePresence initial={false} custom={slideDirection}>
        <motion.div
          key={clampedIndex}
          custom={slideDirection}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          style={{ width: size, height: size, position: 'absolute', top: 0, left: 0 }}
        >
          {clampedIndex === generatedIndex ? (
            <StylizedQRCodeDisplay url={fallbackUrl} size={size} />
          ) : (
            <img
              src={carouselImages[clampedIndex]}
              alt={`QR Code ${clampedIndex + 1}`}
              style={{
                width: size,
                height: size,
                objectFit: 'contain',
              }}
              draggable={false}
            />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default CustomQRDisplay;
